import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Table, Button, Form, InputGroup, Row, Col, Alert, Modal, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faFilter, faPlus, faEye, faEdit, faTrash, faFilePdf,
  faSortUp, faSortDown
} from '@fortawesome/free-solid-svg-icons';
import { guaranteeService } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { formatDate, convertDateForApi, isDateExpired, getDateStatus, calculateGuaranteeDuration } from '../../utils/date-utils';
import PdfPreview from '../common/PdfPreview';
import EmptyStateView from '../common/EmptyStateView';

const ITEMS_PER_PAGE = 50;

const GuaranteeList = () => {
  const navigate = useNavigate();
  const [guarantees, setGuarantees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterModalError, setFilterModalError] = useState(null);
  const [inputSearchTerm, setInputSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  
  // Dynamic pricing states
  const [prices, setPrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(false);
  const [priceCache, setPriceCache] = useState({});
  
  // Input state for filter form
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Active filter state that triggers API calls
  const [activeFromDate, setActiveFromDate] = useState('');
  const [activeToDate, setActiveToDate] = useState('');
  
  // Add states for server-side pagination and sorting
  const [sortBy, setSortBy] = useState('issueDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingGuarantee, setDeletingGuarantee] = useState(null);
  const [selectedGuarantee, setSelectedGuarantee] = useState(null);
  const [selectedGuaranteeData, setSelectedGuaranteeData] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  
  // For testing: force show empty state
  const [forceShowEmptyState, setForceShowEmptyState] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Load guarantees with server-side filtering, sorting, and pagination
  useEffect(() => {
    const fetchGuarantees = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Build query parameters for the backend
        const queryParams = new URLSearchParams();
        queryParams.append('page', currentPage - 1); // Backend uses 0-based indexing
        queryParams.append('size', ITEMS_PER_PAGE);
        queryParams.append('sortBy', sortBy);
        queryParams.append('sortDirection', sortDirection);
        
        // Add search term if present
        if (searchTerm.trim()) {
          queryParams.append('searchTerm', searchTerm.trim());
        }
        
        // Add status filter if not "all"
        if (filter !== 'all') {
          queryParams.append('status', filter);
        }
        
        // Add date filters if present
        if (activeFromDate) {
          const apiFromDate = convertDateForApi(activeFromDate);
          queryParams.append('fromDate', apiFromDate);
        }
        
        if (activeToDate) {
          const apiToDate = convertDateForApi(activeToDate);
          queryParams.append('toDate', apiToDate);
        }
        
        // Call the search API endpoint
        const response = await guaranteeService.search(queryParams);
        
        // Update state with the response data
        setGuarantees(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);
        
      } catch (err) {
        console.error("Fetch error:", err);
        setError('Garantien konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchGuarantees();
  }, [currentPage, sortBy, sortDirection, searchTerm, filter, activeFromDate, activeToDate]);
  
  // Fetch prices for guarantees
  useEffect(() => {
    const fetchPrices = async () => {
      if (guarantees.length === 0) return;
      
      setPricesLoading(true);
      const newPrices = {};
      
      try {
        // Get unique durations from guarantees
        const durations = new Set();
        guarantees.forEach(guarantee => {
          const duration = calculateGuaranteeDuration(guarantee.issueDate, guarantee.expirationDate);
          if (duration) {
            durations.add(duration);
          }
        });
        
        // Fetch prices for each unique duration
        const pricePromises = Array.from(durations).map(async (duration) => {
          // Check cache first
          if (priceCache[duration]) {
            return { duration, price: priceCache[duration] };
          }
          
          try {
            const price = await guaranteeService.getPrices(duration);
            return { duration, price };
          } catch (error) {
            console.error(`Error fetching price for duration ${duration}:`, error);
            return { duration, price: null };
          }
        });
        
        const priceResults = await Promise.all(pricePromises);
        
        // Update price cache and current prices
        const updatedCache = { ...priceCache };
        priceResults.forEach(({ duration, price }) => {
          if (price !== null) {
            newPrices[duration] = price;
            updatedCache[duration] = price;
          }
        });
        
        setPriceCache(updatedCache);
        setPrices(newPrices);
      } catch (error) {
        console.error('Error fetching prices:', error);
      } finally {
        setPricesLoading(false);
      }
    };
    
    fetchPrices();
  }, [guarantees]);
  
  // Handle sort column click
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
    // Reset to first page when sort changes
    setCurrentPage(1);
  };

  // Page change handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of the list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    
    // Previous button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );
    
    // First page
    items.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Ellipsis if needed after first page
    if (currentPage > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }
    
    // Pages around current page
    for (let number = Math.max(2, currentPage - 1); number <= Math.min(totalPages - 1, currentPage + 1); number++) {
      if (number !== 1 && number !== totalPages) {
        items.push(
          <Pagination.Item
            key={number}
            active={currentPage === number}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </Pagination.Item>
        );
      }
    }
    
    // Ellipsis if needed before last page
    if (currentPage < totalPages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }
    
    // Last page (if more than one page)
    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );
    
    return items;
  };

  // Handle delete guarantee
  const handleDelete = async () => {
    if (!deletingGuarantee) return;
    try {
      setLoading(true);
      
      const result = await guaranteeService.delete(deletingGuarantee);
      if (result.success) {
        // Refresh the list after deletion
        // We'll let the useEffect handle loading the updated list
        setCurrentPage(1); // Reset to first page
        
        // Clear any previous errors
        setError(null);
      } else {
        // Handle error from backend
        if (result.data && typeof result.data === 'object') {
          // Handle structured error response
          const errorMessage = result.data.message || 
                              (result.data.status && result.data.status.includes(':') ? 
                               result.data.status.split(':')[1].trim() : 
                               'Garantie konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.');
          setError(errorMessage);
        } else {
          // Handle generic error
          setError(result.message || 'Garantie konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.');
        }
      }
    } catch (err) {
      setError('Garantie konnte nicht gelöscht werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setShowDeleteModal(false);
      setDeletingGuarantee(null);
      setLoading(false);
    }
  };

  // Handle PDF generation
  const handleGeneratePdf = async (template) => {
    if (!selectedGuarantee) return;
    
    try {
      setPdfGenerating(true);
      await guaranteeService.getPdf(selectedGuarantee, template);
      setPdfSuccess(true);
      
      // Reset success message after 2 seconds
      setTimeout(() => {
        setShowPdfModal(false);
        setSelectedGuarantee(null);
        setPdfSuccess(false);
      }, 1500);
    } catch (err) {
      setError('PDF konnte nicht generiert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Handle date change in the filter form
  const handleDateChange = (e, setter) => {
    // Just set the value as typed - we'll validate on submission
    setter(e.target.value);
  };

  // Apply filters from the modal
  const applyFilters = () => {
    // Validate dates before setting them as active
    let validFromDate = '';
    let validToDate = '';
    
    if (fromDate) {
      // Validate format
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(fromDate)) {
        validFromDate = fromDate;
      } else {
        setFilterModalError('Von-Datum muss im Format TT.MM.JJJJ sein');
        return;
      }
    }
    
    if (toDate) {
      // Validate format
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(toDate)) {
        validToDate = toDate;
      } else {
        setFilterModalError('Bis-Datum muss im Format TT.MM.JJJJ sein');
        return;
      }
    }
    
    // Set the active dates that will trigger API calls
    setActiveFromDate(validFromDate);
    setActiveToDate(validToDate);
    
    // Clear filter modal errors
    setFilterModalError(null);
    
    // Reset to first page when filters change
    setCurrentPage(1);
    
    // Close the modal
    setShowFilterModal(false);
  };

  // Clear filters
  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setActiveFromDate('');
    setActiveToDate('');
    setFilter('all');
    setSearchTerm('');
    setInputSearchTerm(''); // Also clear the input search term
    setFilterModalError(null);
    setShowFilterModal(false);
    setCurrentPage(1);
  };

  if (loading && guarantees.length === 0) {
    return <LoadingSpinner />;
  }
  
  const handlePriceConversion = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  }
  
  // Check if we should show the empty state
  const shouldShowEmptyState = forceShowEmptyState || (
    guarantees.length === 0 && !loading && !searchTerm && filter === 'all' && 
    !activeFromDate && !activeToDate
  );
  
  // Handle create new guarantee from empty state
  const handleCreateNewGuarantee = () => {
    navigate('/guarantees/new');
  };
  
  return (
    <Container>
      <h2 className="mb-4">Mobilitätsgarantien</h2>
      
      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}
       {shouldShowEmptyState ? (
        <EmptyStateView onCreateNew={handleCreateNewGuarantee} />
      ) : (
      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3 align-items-center">
            <Col md={6} className="mb-2 mb-md-0">
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Suche nach Bestätigungscode, Kunde, Kennzeichen, VIN..."
                  value={inputSearchTerm}
                  onChange={e => setInputSearchTerm(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setSearchTerm(inputSearchTerm);
                      setCurrentPage(1); // Reset to first page when search is applied
                    }
                  }}
                />
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setSearchTerm(inputSearchTerm);
                    setCurrentPage(1);
                  }}
                >
                  Suchen
                </Button>
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end">
              <Button variant="outline-secondary" className="me-2" onClick={() => setShowFilterModal(true)}>
                <FontAwesomeIcon icon={faFilter} className="me-2" />
                Filter
              </Button>
              <Button variant="primary" onClick={() => navigate('/guarantees/new')}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Neue Garantie
              </Button>
            </Col>
          </Row>
          
          {activeFromDate || activeToDate || filter !== 'all' ? (
            <div className="d-flex mb-3 align-items-center">
              <span className="me-2">Aktive Filter:</span>
              {filter !== 'all' && (
                <span className="badge bg-info me-2">Zustand: {filter === "expired" ? "abgelaufen" : "aktiv"}</span>
              )}
              {activeFromDate && (
                <span className="badge bg-info me-2">Von: {activeFromDate}</span>
              )}
              {activeToDate && (
                <span className="badge bg-info me-2">Bis: {activeToDate}</span>
              )}
              <Button variant="link" size="sm" onClick={clearFilters}>Alle löschen</Button>
            </div>
          ) : null}
          
          {/* Results summary and pagination information */}
          <div className="d-flex justify-content-between mb-3">
            <div>
              <small className="text-muted">
                {totalElements === 0 ? 
                  'Keine Ergebnisse gefunden' : 
                  `Anzeige ${(currentPage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(currentPage * ITEMS_PER_PAGE, totalElements)} von ${totalElements} Ergebnissen`}
              </small>
            </div>
            <div className="pagination-container">
              {totalPages > 1 ? (
                <Pagination size="sm" className="mb-0">
                  {renderPaginationItems()}
                </Pagination>
              ) : (
                <div className="pagination-placeholder"></div>
              )}
            </div>
          </div>
          <div className="table-container">
            {guarantees.length === 0 ? (
              <Alert variant="info">
                Keine Mobilitätsgarantien gefunden, die Ihren Kriterien entsprechen.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th onClick={() => handleSort('confirmationCode')} className="sortable-header">
                        Bestätigungscode
                        {sortBy === 'confirmationCode' && (
                          <FontAwesomeIcon 
                            icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                            className="ms-1" 
                          />
                        )}
                      </th>
                      <th onClick={() => handleSort('customerName')} className="sortable-header">
                        Kunde
                        {sortBy === 'customerName' && (
                          <FontAwesomeIcon 
                            icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                            className="ms-1" 
                          />
                        )}
                      </th>
                      <th onClick={() => handleSort('licensePlate')} className="sortable-header">
                        Kennzeichen / VIN
                        {sortBy === 'licensePlate' && (
                          <FontAwesomeIcon 
                            icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                            className="ms-1" 
                          />
                        )}
                      </th>
                      <th onClick={() => handleSort('issueDate')} className="sortable-header">
                        Ausstellungsdatum
                        {sortBy === 'issueDate' && (
                          <FontAwesomeIcon 
                            icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                            className="ms-1" 
                          />
                        )}
                      </th>
                      <th onClick={() => handleSort('expirationDate')} className="sortable-header">
                        Ablaufdatum
                        {sortBy === 'expirationDate' && (
                          <FontAwesomeIcon 
                            icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                            className="ms-1" 
                          />
                        )}
                      </th>
                      <th onClick={() => handleSort('price')} className="sortable-header text-end">
                        Preis
                        {sortBy === 'price' && (
                          <FontAwesomeIcon 
                            icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                            className="ms-1" 
                          />
                        )}
                      </th>
                      <th className="text-center">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guarantees.map(guarantee => {
                      // Get expiration status using the utility function
                      const dateStatus = getDateStatus(guarantee.expirationDate);
                      
                      // Get dynamic price based on guarantee duration
                      const duration = calculateGuaranteeDuration(guarantee.issueDate, guarantee.expirationDate);
                      const dynamicPrice = duration && prices[duration] ? prices[duration] : null;
                      
                      return (
                        <tr key={guarantee.confirmationCode}>
                          <td>{guarantee.confirmationCode}</td>
                          <td>{guarantee.customerName}</td>
                          <td>
                            {guarantee.licensePlate}
                            {guarantee.vin && (
                              <div className="small text-muted">VIN: {guarantee.vin}</div>
                            )}
                          </td>
                          <td>{formatDate(guarantee.issueDate)}</td>
                          <td>
                            {formatDate(guarantee.expirationDate)}
                            {dateStatus.isExpired && (
                              <span className={`badge ${dateStatus.badgeClass} ms-2`}>
                                {dateStatus.badgeText}
                              </span>
                            )}
                          </td>
                          <td align="right">
                            {pricesLoading ? (
                              <span className="text-muted">Lädt...</span>
                            ) : dynamicPrice !== null ? (
                              handlePriceConversion(dynamicPrice)
                            ) : (
                              handlePriceConversion(guarantee.price)
                            )}
                          </td>
                          <td>
                            <div className="d-flex justify-content-center">
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-1"
                                onClick={() => navigate(`/guarantees/${guarantee.confirmationCode}`)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                className="me-1"
                                onClick={() => navigate(`/guarantees/edit/${guarantee.confirmationCode}`)}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                className="me-1"
                                onClick={() => {
                                  setDeletingGuarantee(guarantee.confirmationCode);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                              <Button 
                                variant="outline-success" 
                                size="sm"
                                onClick={async () => {
                                  try {
                                    setSelectedGuarantee(guarantee.confirmationCode);
                                    
                                    // Fetch the full guarantee details for PDF generation
                                    const guaranteeDetails = await guaranteeService.getById(guarantee.confirmationCode);
                                    setSelectedGuaranteeData(guaranteeDetails);
                                    
                                    // Show the PDF modal
                                    setShowPdfModal(true);
                                  } catch (err) {
                                    setError('Garantiedetails für die PDF-Generierung konnten nicht geladen werden.');
                                  }
                                }}
                              >
                                <FontAwesomeIcon icon={faFilePdf} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
          
          {/* Bottom pagination */}
          <div className="pagination-container mt-3">
            {totalPages > 1 ? (
              <Pagination>
                {renderPaginationItems()}
              </Pagination>
            ) : (
              <div className="pagination-placeholder"></div>
            )}
          </div>
        </Card.Body>
      </Card>
      )}
      
      {/* Filter Modal */}
      <Modal 
        show={showFilterModal} 
        onHide={() => {
          setShowFilterModal(false);
          setFilterModalError(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Garantien filtern</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {filterModalError && (
            <ErrorMessage 
              message={filterModalError} 
              onDismiss={() => setFilterModalError(null)} 
            />
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select 
                value={filter} 
                onChange={e => setFilter(e.target.value)}
              >
                <option value="all">Alle Garantien</option>
                <option value="active">Nur aktive</option>
                <option value="expired">Nur abgelaufene</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Ausstellungsdatum von (TT.MM.JJJJ)</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="TT.MM.JJJJ"
                value={fromDate} 
                onChange={e => handleDateChange(e, setFromDate)}
              />
              <Form.Text className="text-muted">
                Datum im Format TT.MM.JJJJ eingeben (z.B. 31.03.2025)
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Ausstellungsdatum bis (TT.MM.JJJJ)</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="TT.MM.JJJJ"
                value={toDate} 
                onChange={e => handleDateChange(e, setToDate)}
              />
              <Form.Text className="text-muted">
                Datum im Format TT.MM.JJJJ eingeben (z.B. 31.03.2025)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={clearFilters}>
            Filter löschen
          </Button>
          <Button variant="primary" onClick={applyFilters}>
            Filter anwenden
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Löschen bestätigen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sind Sie sicher, dass Sie diese Mobilitätsgarantie löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* PDF Preview Modal */}
      <PdfPreview
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        onSelect={handleGeneratePdf}
        loading={pdfGenerating}
        success={pdfSuccess}
      />
      
      {/* CSS for sortable headers */}
      <style jsx>{`
        .sortable-header {
          cursor: pointer;
          user-select: none;
        }
        
        .sortable-header:hover {
          background-color: #f1f1f1;
        }
      `}</style>
    </Container>
  );
};

export default GuaranteeList;